import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PLATFORM_COUNTRY_CODES, getCountryLabel } from '../config/countries';
import { getCountryFlag } from '../utils/countryFlags';
import styles from './SearchableCountrySelect.module.css';

/**
 * Select pays avec recherche (autocomplete).
 * value et onChange utilisent le code pays en majuscules (ex: 'FR'). Passer locale pour les libellés dans la langue du site.
 * En compactDisplay, à l'ouverture un overlay pleine largeur permet de taper et voir la liste correctement.
 */
export default function SearchableCountrySelect({
  value = '',
  onChange,
  locale = 'fr',
  placeholder = 'Sélectionner un pays',
  allowEmpty = false,
  noResultsLabel = 'Aucun pays trouvé',
  className = '',
  inputClassName = '',
  compactDisplay = false,
}) {
  const code = value ? value.trim().toUpperCase() : '';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const overlayInputRef = useRef(null);

  const displayValue = code
    ? (compactDisplay && !open ? getCountryFlag(code) : `${getCountryFlag(code)}    ${getCountryLabel(code, locale)}`)
    : '';

  const filtered = PLATFORM_COUNTRY_CODES.filter((c) => {
    const label = getCountryLabel(c, locale).toLowerCase();
    const term = search.trim().toLowerCase();
    return !term || label.includes(term) || c.toLowerCase().includes(term);
  });

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // En mode compact, focus l'input de l'overlay à l'ouverture
  useEffect(() => {
    if (compactDisplay && open && overlayInputRef.current) {
      overlayInputRef.current.focus();
    }
  }, [compactDisplay, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current && containerRef.current.contains(e.target);
      const inOverlay = overlayRef.current && overlayRef.current.contains(e.target);
      if (!inTrigger && !inOverlay) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (selectedCode) => {
    onChange(selectedCode || '');
    setOpen(false);
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    if (!open) setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      e.target.blur();
    }
  };

  const renderListbox = (listboxClassName, listboxId = 'country-listbox') => (
    <ul
      id={listboxId}
      role="listbox"
      className={listboxClassName}
    >
      {allowEmpty && (
        <li
          role="option"
          className={styles.option}
          onClick={() => handleSelect('')}
          onMouseDown={(e) => e.preventDefault()}
            >
              —
            </li>
      )}
      {filtered.map((c) => (
        <li
          key={c}
          role="option"
          aria-selected={c === code}
          className={`${styles.option} ${c === code ? styles.optionSelected : ''}`}
          onClick={() => handleSelect(c)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <span className={styles.optionFlag}>{getCountryFlag(c)}</span> {getCountryLabel(c, locale)}
        </li>
      ))}
      {filtered.length === 0 && (
        <li className={styles.optionEmpty}>{noResultsLabel}</li>
      )}
    </ul>
  );

  const useFullWidthOverlay = compactDisplay && open;

  const overlayContent = useFullWidthOverlay && (
    <div
      className={styles.overlayBackdrop}
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <div
        ref={overlayRef}
        className={styles.overlayPanel}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.overlayClose}
          onClick={() => setOpen(false)}
          aria-label="Fermer"
        >
          ×
        </button>
        <input
          ref={overlayInputRef}
          type="text"
          role="combobox"
          aria-expanded={true}
          aria-autocomplete="list"
          aria-controls="country-listbox-overlay"
          aria-label={placeholder}
          value={search}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`${styles.input} ${styles.overlayInput}`}
          autoComplete="off"
        />
        {renderListbox(`${styles.listbox} ${styles.listboxOverlay}`, 'country-listbox-overlay')}
      </div>
    </div>
  );

  return (
    <>
      <div className={`${styles.wrapper} ${compactDisplay ? styles.wrapperCompact : ''} ${className}`} ref={containerRef}>
        {compactDisplay ? (
          <button
            type="button"
            className={`${styles.triggerCompact} ${inputClassName}`}
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={code ? getCountryLabel(code, locale) : 'Sélectionner un pays'}
          >
            <span className={styles.triggerFlag}>{code ? getCountryFlag(code) : '—'}</span>
            <span className={styles.triggerArrow} aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        ) : (
          <input
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={open && !useFullWidthOverlay ? 'country-listbox' : undefined}
            id="country-search"
            value={open && !useFullWidthOverlay ? search : displayValue}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`${styles.input} ${inputClassName}`}
            autoComplete="off"
            readOnly={!open}
          />
        )}
        {open && !useFullWidthOverlay && renderListbox(styles.listbox)}
      </div>
      {overlayContent && createPortal(overlayContent, document.body)}
    </>
  );
}
